import React from 'react';

import { useCustomerStatsQuery } from 'src/app/admin/state/api/adminGraphSlice';
import StatsPanel from 'src/app/admin/components/stats-panel/StatsPanel';
import StatTile from 'src/app/admin/components/stats-panel/StatTile';
import UserBreakdown from 'src/app/admin/components/stats-panel/UserBreakdown';
import GeneratedAtLabel from 'src/app/admin/components/stats-panel/GeneratedAtLabel';
import { subscriptionTierLabel } from 'src/app/admin/components/stats-panel/subscriptionTierLabel';

type OrgStatsPanelProps = {
    customerId: string;
};

/**
 * What an organisation owns, and who loses access if it goes. Everything here is shown to whoever
 * can reach the page, which the route already restricts to super admins — the same people allowed
 * to delete the organisation, who need these figures to judge what that would cost.
 */
const OrgStatsPanel = ({ customerId }: OrgStatsPanelProps) => {
    const { data: stats, isLoading, isError } = useCustomerStatsQuery({ customerId });

    const tiers = stats?.projectsBySubscription ?? [];

    return (
        <section className='flex flex-col gap-3' data-testid='org-stats-panel'>
            <StatsPanel
                title='What this organisation owns'
                isLoading={isLoading}
                isError={isError}
                figures={[
                    { label: 'Projects', value: stats?.projectCount ?? 0 },
                    { label: 'Samples', value: stats?.sampleCount ?? 0 },
                    { label: 'Portfolios', value: stats?.portfolioCount ?? 0 },
                    // The split belongs to the figure it qualifies, not adrift beneath the row.
                    { label: 'Users', value: stats?.users.total ?? 0, note: stats && <UserBreakdown users={stats.users} /> },
                ]}
            />

            {tiers.length > 0 && (
                <>
                    <span className='text-xl text-black'>Projects by subscription</span>
                    <div className='grid grid-cols-[repeat(auto-fit,minmax(150px,1fr))] gap-4'>
                        {tiers.map(tier => (
                            <StatTile key={tier.subscription} label={subscriptionTierLabel(tier.subscription)} value={tier.count} />
                        ))}
                    </div>
                </>
            )}

            <GeneratedAtLabel generatedAt={stats?.generatedAt} />
        </section>
    );
};

export default OrgStatsPanel;
